/**
 * Design Tokens
 * Centralized design system values for consistent styling across the app
 */

// Color Palette
export const colors = {
  // Primary Colors (כחול)
  primary: {
    50: '#f0f9ff',
    100: '#e0f2fe',
    200: '#bae6fd',
    300: '#7dd3fc',
    400: '#38bdf8',
    500: '#0ea5e9', // Main primary color
    600: '#0284c7', // Hover state
    700: '#0369a1',
    800: '#075985',
    900: '#0c4a6e',
  },

  // Accent Colors (צהוב זהב)
  accent: {
    50: '#fefce8',
    100: '#fef9c3',
    200: '#fef08a',
    300: '#fde047',
    400: '#facc15',
    500: '#eab308', // Main accent color
    600: '#ca8a04',
    700: '#a16207',
    800: '#854d0e',
    900: '#713f12',
  },

  // Status Colors
  success: {
    50: '#f0fdf4',
    100: '#dcfce7',
    200: '#bbf7d0',
    500: '#22c55e',
    600: '#16a34a',
    700: '#15803d',
    800: '#166534',
  },

  warning: {
    50: '#fff7ed',
    100: '#ffedd5',
    200: '#fed7aa',
    500: '#f97316',
    600: '#ea580c',
    700: '#c2410c',
  },

  danger: {
    50: '#fef2f2',
    100: '#fee2e2',
    200: '#fecaca',
    500: '#ef4444',
    600: '#dc2626',
    700: '#b91c1c',
    800: '#991b1b',
  },

  // Neutral Colors (אפור)
  gray: {
    50: '#f9fafb',
    100: '#f3f4f6',
    200: '#e5e7eb',
    300: '#d1d5db',
    400: '#9ca3af',
    500: '#6b7280',
    600: '#4b5563',
    700: '#374151',
    800: '#1f2937',
    900: '#111827',
  },

  // Israeli cultural colors
  israeli: {
    blue: '#0038a8',
    lightblue: '#63b3ed',
    sand: '#f7fafc',
    stone: '#718096',
    olive: '#68d391',
  },
} as const

// Typography
export const typography = {
  fontFamily: {
    hebrew: ['Arial Hebrew', 'David', 'Times New Roman', 'serif'],
    sans: ['Inter', 'system-ui', 'Avenir', 'Helvetica', 'Arial', 'sans-serif'],
  },

  fontSize: {
    xs: '12px',
    sm: '14px',
    base: '16px',
    lg: '18px',
    xl: '20px',
    '2xl': '24px',
    '3xl': '30px',
    '4xl': '36px',
    '5xl': '48px',
  },

  fontWeight: {
    light: 300,
    normal: 400,
    medium: 500,
    semibold: 600,
    bold: 700,
  },

  lineHeight: {
    tight: 1.2,
    normal: 1.5,
    hebrew: 1.7, // Hebrew text needs more line height
    relaxed: 1.75,
  },
} as const

// Spacing Scale (4px base)
export const spacing = {
  xs: '8px',
  sm: '12px',
  md: '16px',
  lg: '24px',
  xl: '32px',
  '2xl': '48px',
  '3xl': '64px',
} as const

// Border Radius
export const borderRadius = {
  sm: '4px',
  md: '8px',
  lg: '12px',
  xl: '16px',
  full: '50%',
} as const

// Shadows
export const shadows = {
  sm: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
  md: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
  lg: '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
  xl: '0 20px 25px -5px rgba(0, 0, 0, 0.1)',
} as const

// Z-Index Scale
export const zIndex = {
  hide: -1,
  base: 0,
  dropdown: 10,
  sticky: 20,
  fixed: 30,
  'modal-backdrop': 40,
  'modal': 50,
  'popover': 60,
  'tooltip': 70,
} as const

// Transitions
export const transitions = {
  duration: {
    fast: '150ms',
    base: '200ms',
    slow: '300ms',
    slower: '500ms',
  },
  timing: {
    linear: 'linear',
    ease: 'ease',
    'ease-in': 'ease-in',
    'ease-out': 'ease-out',
    'ease-in-out': 'ease-in-out',
  },
} as const

// Breakpoints
export const breakpoints = {
  xs: '320px',
  sm: '640px',
  md: '768px',
  lg: '1024px',
  xl: '1280px',
  '2xl': '1536px',
} as const

// Component-specific tokens
export const components = {
  button: {
    primary: {
      bg: colors.primary[600],
      bgHover: colors.primary[700],
      text: '#ffffff',
      padding: `${spacing.md} ${spacing.lg}`,
      borderRadius: borderRadius.lg,
      fontSize: typography.fontSize.base,
      fontWeight: typography.fontWeight.semibold,
    },
    secondary: {
      bg: colors.gray[200],
      bgHover: colors.gray[300],
      text: colors.gray[800],
      padding: `${spacing.md} ${spacing.lg}`,
      borderRadius: borderRadius.lg,
      fontSize: typography.fontSize.base,
      fontWeight: typography.fontWeight.semibold,
    },
    danger: {
      bg: colors.danger[600],
      bgHover: colors.danger[700],
      text: '#ffffff',
      padding: `8px 16px`,
      borderRadius: borderRadius.md,
      fontSize: typography.fontSize.sm,
      fontWeight: typography.fontWeight.semibold,
    },
  },

  card: {
    bg: '#ffffff',
    borderRadius: borderRadius.lg,
    shadow: shadows.md,
    shadowHover: shadows.lg,
    padding: spacing.lg,
  },

  input: {
    bg: '#ffffff',
    border: colors.gray[300],
    borderFocus: colors.primary[500],
    text: colors.gray[900],
    placeholder: colors.gray[400],
    padding: `${spacing.md}`,
    borderRadius: borderRadius.lg,
  },

  modal: {
    overlay: 'rgba(0, 0, 0, 0.5)',
    bg: '#ffffff',
    borderRadius: borderRadius.xl,
    shadow: shadows.xl,
    padding: '24px',
  },

  alert: {
    error: {
      bg: colors.danger[50],
      border: colors.danger[200],
      text: colors.danger[800],
      icon: colors.danger[600],
    },
    warning: {
      bg: colors.warning[50],
      border: colors.warning[100],
      text: colors.warning[700],
      icon: colors.warning[600],
    },
    success: {
      bg: colors.success[50],
      border: colors.success[200],
      text: colors.success[800],
      icon: colors.success[600],
    },
    info: {
      bg: colors.primary[50],
      border: colors.primary[200],
      text: colors.primary[800],
      icon: colors.primary[600],
    },
  },
} as const

// Header Styles
export const headers = {
  h1: {
    fontSize: typography.fontSize['3xl'],
    fontWeight: typography.fontWeight.bold,
    lineHeight: typography.lineHeight.tight,
    color: colors.gray[900],
  },
  h2: {
    fontSize: typography.fontSize['2xl'],
    fontWeight: typography.fontWeight.bold,
    lineHeight: typography.lineHeight.tight,
    color: colors.gray[900],
  },
  h3: {
    fontSize: typography.fontSize.xl,
    fontWeight: typography.fontWeight.semibold,
    lineHeight: typography.lineHeight.tight,
    color: colors.gray[900],
  },
} as const

// Text Styles
export const text = {
  body: {
    fontSize: typography.fontSize.base,
    lineHeight: typography.lineHeight.hebrew,
    color: colors.gray[700],
  },
  small: {
    fontSize: typography.fontSize.sm,
    lineHeight: typography.lineHeight.normal,
    color: colors.gray[600],
  },
  large: {
    fontSize: typography.fontSize.lg,
    lineHeight: typography.lineHeight.hebrew,
    color: colors.gray[800],
  },
} as const

/**
 * Tailwind Class Helpers
 * These are CSS class strings ready to use in JSX
 */
export const tailwindClasses = {
  // Button classes
  btnPrimary: 'px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 font-hebrew font-semibold transition-colors',
  btnSecondary: 'px-6 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 font-hebrew font-semibold transition-colors',
  btnDanger: 'px-4 py-2 bg-danger-600 text-white rounded-lg hover:bg-danger-700 font-hebrew font-semibold text-sm transition-colors',
  btnGhost: 'px-4 py-2 text-primary-600 hover:bg-primary-50 rounded-lg font-hebrew font-semibold transition-colors',

  // Card classes
  card: 'bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow',
  cardGradient: 'bg-gradient-to-l from-primary-600 to-primary-500 text-white px-6 py-8 rounded-lg shadow-lg',
  cardInfo: 'bg-primary-50 border border-primary-200 rounded-lg p-4',
  cardSuccess: 'bg-success-50 border border-success-200 rounded-lg p-4',

  // Form classes
  input: 'w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent font-hebrew transition-all',
  label: 'block text-sm font-semibold text-gray-700 mb-2 font-hebrew',
  select: 'w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent font-hebrew',

  // Modal classes
  modalOverlay: 'fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4',
  modalContent: 'bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto',
  modalHeader: 'bg-gradient-to-l from-primary-600 to-primary-500 px-6 py-4 text-white font-hebrew',

  // Badge classes
  badgeSuccess: 'inline-flex items-center px-3 py-1 bg-success-100 text-success-800 rounded-full text-sm font-semibold font-hebrew',
  badgeWarning: 'inline-flex items-center px-3 py-1 bg-warning-100 text-warning-800 rounded-full text-sm font-semibold font-hebrew',
  badgeDanger: 'inline-flex items-center px-3 py-1 bg-danger-100 text-danger-800 rounded-full text-sm font-semibold font-hebrew',

  // Layout classes
  container: 'max-w-6xl mx-auto px-4',
  gridCols: 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4',

  // Text classes
  heading1: 'text-3xl md:text-5xl font-bold text-gray-900 font-hebrew mb-2',
  heading2: 'text-2xl font-bold text-gray-900 font-hebrew mb-6',
  heading3: 'text-xl font-bold text-gray-900 mb-4 font-hebrew',

  // Alert classes
  alertError: 'bg-danger-50 border border-danger-200 rounded-lg p-4 flex gap-3',
  alertWarning: 'bg-warning-50 border border-warning-100 rounded-lg p-4 flex gap-3',
  alertSuccess: 'bg-success-50 border border-success-200 rounded-lg p-4 flex gap-3',
  alertInfo: 'bg-primary-50 border border-primary-200 rounded-lg p-4 flex gap-3',
  alertIcon: 'w-5 h-5 flex-shrink-0 mt-0.5',
  alertText: 'text-sm font-hebrew font-semibold',
} as const

export default {
  colors,
  typography,
  spacing,
  borderRadius,
  shadows,
  zIndex,
  transitions,
  breakpoints,
  components,
  headers,
  text,
  tailwindClasses,
}
